import React, { useMemo } from 'react';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';
import * as am4charts from '@amcharts/amcharts4/charts';

import { IAccessPointRssiTrend } from '../../../../services/core/accessPoints';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import { LineChart } from '@components/chart/lineChart';
import { drawAccessPointSamplesXAxisLabels, formatTooltipHour } from './coreAccessPointSamples';
import { DateRange } from '..';
import './charts.css';

interface IProps {
  data: IAccessPointRssiTrend[],
  dateRange: DateRange,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart
}

type RssiDataWithLocal = IAccessPointRssiTrend & { local: number };

export const getMeanRssiTooltipAdapter = (t: TTypedTFunction) => {
  return (_: any, target: am4charts.LineSeries) => {
    const dataContext = target.tooltipDataItem?.dataContext as RssiDataWithLocal;
    if (!dataContext) return '';
    const date = formatTooltipHour(dataContext.local);
    return `<div class="column-date-chart-tooltip-container"><span class="column-date-chart__tooltip-title">${date}</span>
      <br/><span><div class="chart-key" style="--colour: #5b9ad2"></div>&nbsp;${t('MEAN_RSSI_DBM')}: ${dataContext.rssi}</span></div>`;
  };
};

export const CoreAccessPointMeanRssi = (props: IProps) => {
  const { data, dateRange } = props;
  const { t } = useTranslation('coreAccessPoints');

  const rssiData = useMemo(() => {
    const filtered = data.some(d => d.rssi !== null) ? data : [];
    return filtered.map(d => ({ ...d, local: d.date }));
  }, [data]);

  return (
    <div className="core-access-point-chart-section">
      <LineChart<RssiDataWithLocal>
        data={rssiData}
        className="core-ap-mean-rssi-by-time-chart"
        series={[{
          dataKey: 'rssi',
          description: t('MEAN_RSSI_DBM'),
          visible: true,
          colour: '#5b9ad2',
          bulletsOnly: true,
        }]}
        title={
          <>
            {t('MEAN_RSSI_BY_TIME')}{' '}
            <Tooltip popperContent={t('MEAN_RSSI_BY_TIME_TOOLTIP')} placement="right">
              <i className="fa fa-question-circle" style={{ color: '#7490b9', cursor: 'pointer' }} />
            </Tooltip>
          </>
        }
        onMount={props.onMount}
        cypressId={'coreAccessPointMeanRssiByTime'}
        hasLegend={true}
        drawXAxis={(_, xAxis) => drawAccessPointSamplesXAxisLabels(xAxis, dateRange)}
        drawYAxis={(_, axis) => {
          const valueAxis = axis as am4charts.ValueAxis;
          valueAxis.min = -150;
          valueAxis.max = 0;
        }}
        getSeriesToolTipAdapter={() => getMeanRssiTooltipAdapter(t)}
        useAxisTooltip
        bottomBorder
        invertedXAxis
        adjustChartDimensions = {{ height: 240 }}
      />
    </div>
  );
};
